// globalErrorHandler.js
import axios from "axios";
import { toast } from "react-toastify";
import { showGlobalErrorToast, showSuccessToast } from "./utils/toastUtils"; // use global error toast
import { TOAST_MESSAGES } from "./utils/toastMessages";

/**
 * Initialize global error handling
 * @param {axiosInstance} api - your Axios instance
 */
export const initGlobalErrorHandler = (api) => {
  // 1️⃣ Axios response interceptor
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // ✅ Skip showing toast if request is marked as silent
      if (originalRequest?.silent) {
        return Promise.reject(error);
      }

      //  No internet connection
      if (!navigator.onLine) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.NO_INTERNET);
        return Promise.reject(error);
      }

      //  Unauthorized / session expired
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const refreshToken = localStorage.getItem("refresh_token");

        if (!refreshToken) {
          //   showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.UNAUTHORIZED);
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/";
          return Promise.reject(error);
        }

        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const newAccessToken = res.data.access_token;

          localStorage.setItem("access_token", newAccessToken);
          sessionStorage.setItem("access_token", newAccessToken);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          return api(originalRequest);
        } catch (err) {
          showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.UNAUTHORIZED);
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/";
          return Promise.reject(err);
        }
      }

      //  Forbidden / inactive user
      if (error.response?.status === 403) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.FORBIDDEN);
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/";
        return Promise.reject(error);
      }

      // Unstable connection (internet is on but no response)
      if (navigator.onLine && !error.response) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.UNSTABLE_CONNECTION);
        return Promise.reject(error);
      }

      //  Too many requests
      if (error.response?.status === 429) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.TOO_MANY_REQUESTS);
        return Promise.reject(error);
      }

      //  Server errors 5xx
      if (error.response?.status >= 500) {
        // showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.SERVER_ERROR);
        return Promise.reject(error);
      }

      //  Maintenance mode
      if (
        error.response?.status === 503 &&
        error.response.headers["x-maintenance"]
      ) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.MAINTENANCE);
        return Promise.reject(error);
      }

      //  Request timeout
      if (error.code === "ECONNABORTED") {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.REQUEST_TIMEOUT);
        return Promise.reject(error);
      }

      // Default error
      // const msg =
      //   error.response?.data?.message || TOAST_MESSAGES.GLOBAL.SERVER_ERROR;
      // showGlobalErrorToast(msg);

      return Promise.reject(error);
    }
  );

  // 2️⃣ Global JS errors
  window.onerror = (msg, url, line, col, error) => {
    showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.JS_ERROR);
    console.error(error || msg);
  };

  // 3️⃣ Unhandled promise rejections
  window.onunhandledrejection = (event) => {
    showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.PROMISE_REJECTION);
    console.error(event.reason);
  };

  // 4️⃣ Offline / online events
  window.addEventListener("offline", () => {
    showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.NO_INTERNET);
  });

  window.addEventListener("online", () => {
    toast.dismiss(); // remove offline toast
    showSuccessToast(TOAST_MESSAGES.GLOBAL.BACK_ONLINE);
  });
};
